/**
 * Runtime state for the editable `b2b-users` architecture-map stage.
 *
 * Wraps `useArchitectureWorkspace` and exposes typed nodes/edges/positions
 * for the b2b-users stage only. Persists every mutation (debounced via the
 * caller — the page hook batches drag-end updates).
 */

import { useCallback, useMemo } from "react";

import { useArchitectureWorkspace } from "@/application/use-cases/architecture/use-architecture-workspace.queries";
import {
  b2bUsersEdgesToJson,
  b2bUsersNodesToJson,
  b2bUsersPositionsToJson,
  parseB2bUsersEdges,
  parseB2bUsersNodes,
  parseWorkspacePositions,
  type WorkspacePosition,
} from "@/infrastructure/repositories/architecture-workspace.mappers";
import {
  B2B_USERS_SEED_EDGES,
  B2B_USERS_SEED_NODES,
  type B2bUsersEdge,
  type B2bUsersNode,
} from "@/presentation/data/b2b-users-stage-seed";

export interface UseB2bUsersStage {
  nodes: B2bUsersNode[];
  edges: B2bUsersEdge[];
  positions: Record<string, WorkspacePosition>;
  isLoading: boolean;
  setNodes: (next: readonly B2bUsersNode[]) => void;
  setEdges: (next: readonly B2bUsersEdge[]) => void;
  setPositions: (next: Record<string, WorkspacePosition>) => void;
  resetToSeed: () => void;
}

export function useB2bUsersStage(): UseB2bUsersStage {
  const { workspace, isLoading, patch } = useArchitectureWorkspace();

  const rawNodes = useMemo(
    () => (workspace ? parseB2bUsersNodes(workspace.b2b_users_nodes) : []),
    [workspace]
  );
  const rawEdges = useMemo(
    () => (workspace ? parseB2bUsersEdges(workspace.b2b_users_edges) : []),
    [workspace]
  );
  const positions = useMemo(
    () => (workspace ? parseWorkspacePositions(workspace.b2b_users_positions) : {}),
    [workspace]
  );

  // First-visit seed: when the workspace has loaded but the JSONB is empty,
  // hydrate the editor with the seed graph in-memory. We do NOT auto-persist
  // here — the first user mutation triggers persistence with the seed already
  // present in component state.
  // CRITICAL: memoize so identity is stable across renders; otherwise the
  // downstream effect that mirrors these into xyflow state will loop.
  const nodes = useMemo(
    () => (rawNodes.length === 0 ? B2B_USERS_SEED_NODES.slice() : rawNodes),
    [rawNodes]
  );
  const edges = useMemo(
    () =>
      rawNodes.length === 0 && rawEdges.length === 0 ? B2B_USERS_SEED_EDGES.slice() : rawEdges,
    [rawNodes, rawEdges]
  );

  const setNodes = useCallback(
    (next: readonly B2bUsersNode[]) => {
      patch({ b2b_users_nodes: b2bUsersNodesToJson(next) });
    },
    [patch]
  );

  const setEdges = useCallback(
    (next: readonly B2bUsersEdge[]) => {
      patch({ b2b_users_edges: b2bUsersEdgesToJson(next) });
    },
    [patch]
  );

  const setPositions = useCallback(
    (next: Record<string, WorkspacePosition>) => {
      patch({ b2b_users_positions: b2bUsersPositionsToJson(next) });
    },
    [patch]
  );

  const resetToSeed = useCallback(() => {
    patch({
      b2b_users_nodes: b2bUsersNodesToJson(B2B_USERS_SEED_NODES),
      b2b_users_edges: b2bUsersEdgesToJson(B2B_USERS_SEED_EDGES),
      b2b_users_positions: b2bUsersPositionsToJson({}),
    });
  }, [patch]);

  return {
    nodes,
    edges,
    positions,
    isLoading,
    setNodes,
    setEdges,
    setPositions,
    resetToSeed,
  };
}
